// src/services/orderDeskErrorHandler.js
// Comprehensive error handling and monitoring for OrderDesk operations

import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase.js';

class OrderDeskErrorHandler {
    constructor() {
        this.errorCounts = new Map();
        this.errorHistory = [];
        this.maxHistorySize = 500;
        this.retryStrategies = new Map();
        this.setupDefaultRetryStrategies();
    }

    // ═══════════════════════════════════════════════════════════════════════════════
    // ERROR CATEGORIZATION
    // ═══════════════════════════════════════════════════════════════════════════════

    /**
     * Categorize error by type and severity
     */
    categorizeError(error) {
        const status = error.status || 0;
        const message = (error.message || '').toLowerCase();
        
        // Rate limiting errors
        if (status === 429) {
            return {
                category: 'rate_limiting',
                severity: 'medium',
                retryable: true,
                retryDelay: this.extractRetryDelay(error) || 2000
            };
        }
        
        // Authentication errors
        if (status === 401 || status === 403) {
            return {
                category: 'authentication',
                severity: 'high',
                retryable: false,
                requiresAction: 'check_api_credentials'
            };
        }
        
        // Not found errors
        if (status === 404) {
            return {
                category: 'not_found',
                severity: 'medium',
                retryable: false,
                requiresAction: 'verify_order_exists'
            };
        }
        
        // Validation errors
        if (status >= 400 && status < 500) {
            return {
                category: 'validation',
                severity: 'medium',
                retryable: false,
                requiresAction: 'fix_request_data'
            };
        }
        
        // Server errors
        if (status >= 500) {
            return {
                category: 'server_error',
                severity: 'high',
                retryable: true,
                retryDelay: 5000
            };
        }
        
        // Network/timeout errors
        if (message.includes('timeout') || message.includes('network') || message.includes('fetch')) {
            return {
                category: 'network',
                severity: 'medium',
                retryable: true,
                retryDelay: 3000
            };
        }
        
        // Unknown errors
        return {
            category: 'unknown',
            severity: 'high',
            retryable: true,
            retryDelay: 1000
        };
    }

    /**
     * Extract retry delay from error response
     */
    extractRetryDelay(error) {
        const retryAfter = error.response?.headers?.get('x-retry-after') || 
                          error.response?.headers?.get('retry-after');
        
        if (retryAfter) {
            const seconds = parseInt(retryAfter, 10);
            return Number.isFinite(seconds) ? seconds * 1000 : null;
        }
        
        return null;
    }

    // ═══════════════════════════════════════════════════════════════════════════════
    // ERROR LOGGING
    // ═══════════════════════════════════════════════════════════════════════════════

    /**
     * Log error with context
     */
    async logError(error, context = {}) {
        try {
            const categorization = this.categorizeError(error);
            const timestamp = new Date();
            
            const errorEntry = {
                // Error details
                message: error.message,
                stack: error.stack,
                status: error.status,
                
                // Categorization
                category: categorization.category,
                severity: categorization.severity,
                retryable: categorization.retryable,
                
                // Context
                operation: context.operation,
                orderId: context.orderId,
                sourceId: context.sourceId,
                endpoint: context.endpoint,
                method: context.method,
                
                // Timing
                timestamp: serverTimestamp(),
                timestampLocal: timestamp.toISOString(),
                
                // User context
                userId: context.userId,
                userAgent: context.userAgent,
                
                // Request details
                requestData: context.requestData,
                responseData: error.data
            };
            
            // Store in Firestore
            await addDoc(collection(db, 'orderDeskErrors'), errorEntry);
            
            // Store in memory
            this.errorHistory.push({
                ...errorEntry,
                timestamp: timestamp
            });
            
            // Trim history if too large
            if (this.errorHistory.length > this.maxHistorySize) {
                this.errorHistory = this.errorHistory.slice(-this.maxHistorySize);
            }
            
            // Update error counts
            const countKey = `${categorization.category}_${categorization.severity}`;
            this.errorCounts.set(countKey, (this.errorCounts.get(countKey) || 0) + 1);
            
            console.log(`[OrderDeskErrorHandler] Error logged:`, {
                category: categorization.category,
                severity: categorization.severity,
                operation: context.operation
            });
            
        } catch (logError) {
            console.error(`[OrderDeskErrorHandler] Failed to log error:`, logError);
        }
    }

    // ═══════════════════════════════════════════════════════════════════════════════
    // RETRY LOGIC
    // ═══════════════════════════════════════════════════════════════════════════════

    /**
     * Setup default retry strategies
     */
    setupDefaultRetryStrategies() {
        // Rate limiting: wait and retry
        this.retryStrategies.set('rate_limiting', {
            maxRetries: 5,
            baseDelay: 2000,
            backoffMultiplier: 1.5,
            maxDelay: 30000
        });
        
        // Server errors: exponential backoff
        this.retryStrategies.set('server_error', {
            maxRetries: 3,
            baseDelay: 1000,
            backoffMultiplier: 2,
            maxDelay: 10000
        });
        
        // Network errors: quick retry
        this.retryStrategies.set('network', {
            maxRetries: 3,
            baseDelay: 1000,
            backoffMultiplier: 1.5,
            maxDelay: 5000
        });
        
        // Unknown errors: conservative retry
        this.retryStrategies.set('unknown', {
            maxRetries: 2,
            baseDelay: 2000,
            backoffMultiplier: 2,
            maxDelay: 8000
        });
    }

    /**
     * Execute operation with retry logic
     */
    async executeWithRetry(operation, context = {}) {
        const operationName = context.operation || 'unknown_operation';
        let lastError;
        
        for (let attempt = 0; attempt < 10; attempt++) {
            try {
                console.log(`[OrderDeskErrorHandler] Executing ${operationName} (attempt ${attempt + 1})`);
                
                const result = await operation();
                
                if (attempt > 0) {
                    console.log(`[OrderDeskErrorHandler] ${operationName} succeeded after ${attempt + 1} attempts`);
                }
                
                return result;
                
            } catch (error) {
                lastError = error;
                const categorization = this.categorizeError(error);
                
                console.log(`[OrderDeskErrorHandler] ${operationName} failed (attempt ${attempt + 1}):`, {
                    category: categorization.category,
                    severity: categorization.severity,
                    retryable: categorization.retryable
                });
                
                // Log the error
                await this.logError(error, {
                    ...context,
                    attempt: attempt + 1,
                    operation: operationName
                });
                
                // Check if we should retry
                if (!categorization.retryable) {
                    console.log(`[OrderDeskErrorHandler] ${operationName} not retryable, giving up`);
                    break;
                }
                
                const strategy = this.retryStrategies.get(categorization.category);
                if (!strategy || attempt >= strategy.maxRetries - 1) {
                    console.log(`[OrderDeskErrorHandler] ${operationName} max retries reached`);
                    break;
                }
                
                // Calculate delay
                const delay = Math.min(
                    categorization.retryDelay || 
                    strategy.baseDelay * Math.pow(strategy.backoffMultiplier, attempt),
                    strategy.maxDelay
                );
                
                console.log(`[OrderDeskErrorHandler] Retrying ${operationName} in ${delay}ms`);
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }
        
        // All retries failed
        console.error(`[OrderDeskErrorHandler] ${operationName} failed after all retries:`, lastError);
        throw lastError;
    }

    // ═══════════════════════════════════════════════════════════════════════════════
    // MONITORING & ANALYTICS
    // ═══════════════════════════════════════════════════════════════════════════════

    /**
     * Get error statistics
     */
    getErrorStats(timeRange = '24h') {
        const now = Date.now();
        const rangeMs = this.parseTimeRange(timeRange);
        const cutoff = now - rangeMs;
        
        const recentErrors = this.errorHistory.filter(error => 
            error.timestamp.getTime() > cutoff
        );
        
        const stats = {
            total: recentErrors.length,
            categories: {},
            severities: {},
            operations: {},
            timeRange,
            errorRate: 0
        };
        
        recentErrors.forEach(error => {
            // Count by category
            stats.categories[error.category] = (stats.categories[error.category] || 0) + 1;
            
            // Count by severity
            stats.severities[error.severity] = (stats.severities[error.severity] || 0) + 1;
            
            // Count by operation
            if (error.operation) {
                stats.operations[error.operation] = (stats.operations[error.operation] || 0) + 1;
            }
        });
        
        return stats;
    }

    /**
     * Get recent errors
     */
    getRecentErrors(limit = 50) {
        return this.errorHistory.slice(-limit).reverse();
    }

    /**
     * Check if error rate is too high
     */
    isErrorRateHigh(threshold = 0.1, timeRange = '1h') {
        const stats = this.getErrorStats(timeRange);
        const totalOperations = this.getTotalOperations(timeRange);
        
        if (totalOperations === 0) return false;
        
        const errorRate = stats.total / totalOperations;
        return errorRate > threshold;
    }

    /**
     * Parse time range string to milliseconds
     */
    parseTimeRange(timeRange) {
        const match = timeRange.match(/^(\d+)([hmsd])$/);
        if (!match) return 24 * 60 * 60 * 1000; // Default to 24 hours
        
        const value = parseInt(match[1], 10);
        const unit = match[2];
        
        switch (unit) {
            case 's': return value * 1000;
            case 'm': return value * 60 * 1000;
            case 'h': return value * 60 * 60 * 1000;
            case 'd': return value * 24 * 60 * 60 * 1000;
            default: return 24 * 60 * 60 * 1000;
        }
    }

    /**
     * Get total operations count (would need to be tracked separately)
     */
    getTotalOperations(timeRange) {
        // This would be implemented with actual operation tracking
        // For now, return a reasonable estimate
        return Math.max(this.errorHistory.length * 10, 1);
    }

    /**
     * Clear error history (for maintenance)
     */
    clearErrorHistory() {
        this.errorHistory = [];
        this.errorCounts.clear();
        console.log(`[OrderDeskErrorHandler] Error history cleared`);
    }

    /**
     * Health check
     */
    async healthCheck() {
        const stats = this.getErrorStats('1h');
        const isHealthy = !this.isErrorRateHigh(0.2, '1h'); // 20% error rate threshold
        
        return {
            success: isHealthy,
            message: isHealthy ? 'Error handler operational' : 'High error rate detected',
            stats,
            errorHistory: this.errorHistory.length,
            retryStrategies: Array.from(this.retryStrategies.keys())
        };
    }
}

// Export singleton instance
const orderDeskErrorHandler = new OrderDeskErrorHandler();
export default orderDeskErrorHandler;
